import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Edit, Eye } from 'lucide-react';
import GoogleMap from '../components/GoogleMap';


const FestivalList = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();

  const { data: festivalsData, isLoading } = useQuery('festivals', async () => {
    const response = await axios.get('/api/festivals');
    return response.data;
  });

  const createFestivalMutation = useMutation(
    async (festivalData) => {
      // Server will add creator_id from session
      const response = await axios.post('/api/festivals', festivalData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('festivals');
        setShowCreateForm(false);
        toast.success('Festival created successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create festival');
      }
    }
  );

  const festivals = festivalsData?.festivals || [];

  if (isLoading) {
    return <div className="loading">Loading festivals...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2>Festivals</h2>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary"
        >
          <Plus size={16} style={{ marginRight: '8px' }} />
          Create Festival
        </button>
      </div>

      {showCreateForm && (
        <CreateFestivalForm 
          onSubmit={createFestivalMutation.mutate}
          onCancel={() => setShowCreateForm(false)}
          loading={createFestivalMutation.isLoading}
        />
      )}

      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {festivals.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p>No festivals created yet.</p>
            <button 
              onClick={() => setShowCreateForm(true)}
              className="btn btn-primary"
              style={{ marginTop: '15px' }}
            >
              Create Your First Festival
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Name</th>
                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Dates</th>
                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Status</th>
                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Role</th>
                <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {festivals.map(festival => (
                <tr key={festival.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '15px' }}>
                    <div>
                      <strong>{festival.name}</strong>
                      {festival.location_name && (
                        <div style={{ fontSize: '14px', color: '#666' }}>{festival.location_name}</div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '15px', fontSize: '14px' }}>
                    {new Date(festival.start_date).toLocaleDateString()} - {new Date(festival.end_date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: festival.status === 'published' ? '#d4edda' : '#fff3cd',
                      color: festival.status === 'published' ? '#155724' : '#856404'
                    }}>
                      {festival.status}
                    </span>
                  </td>
                  <td style={{ padding: '15px', fontSize: '14px' }}>
                    {festival.user_role || 'viewer'}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <Link
                        to={`/admin/festivals/${festival.id}/view`}
                        className="btn btn-outline-primary"
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          textDecoration: 'none',
                          border: '1px solid #007bff',
                          color: '#007bff',
                          background: 'white'
                        }}
                        title="View Festival"
                      >
                        <Eye size={14} />
                      </Link>
                      <Link
                        to={`/admin/festivals/${festival.id}/edit`}
                        className="btn btn-secondary"
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          textDecoration: 'none',
                          border: '1px solid #6c757d',
                          color: '#6c757d',
                          background: 'white'
                        }}
                        title="Edit Festival"
                      >
                        <Edit size={14} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const CreateFestivalForm = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    location_name: '',
    location_lat: '',
    location_lng: '',
    zoom_level: 15
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log('Form data before submit:', formData);

    // Convert string coordinates to numbers
    const submitData = {
      ...formData,
      location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
      location_lng: formData.location_lng ? parseFloat(formData.location_lng) : null,
      zoom_level: parseInt(formData.zoom_level) || 15
    };

    console.log('Submit data after conversion:', submitData);
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLocationSelect = useCallback((lat, lng, zoom) => {
    console.log('Location selected:', { lat, lng, zoom });
    setFormData(prevData => {
      const newData = {
        ...prevData,
        location_lat: lat,
        location_lng: lng,
        zoom_level: zoom
      };
      console.log('Updated form data:', newData);
      return newData;
    });
  }, []);

  return (
    <div style={{
      background: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}>
      <h3 style={{ marginBottom: '20px' }}>Create New Festival</h3>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Festival Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Location Name</label>
            <input
              type="text"
              name="location_name"
              value={formData.location_name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="datetime-local"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>End Date *</label>
            <input
              type="datetime-local"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Festival Location</label>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Search for a location or navigate the map to set the festival location. The coordinates will update automatically as you move the map.
          </p>
          <GoogleMap
            onLocationSelect={handleLocationSelect}
            initialLat={formData.location_lat}
            initialLng={formData.location_lng}
            initialZoom={formData.zoom_level}
            height="300px"
          />
          {formData.location_lat && formData.location_lng && (
            <div style={{
              marginTop: '10px',
              padding: '8px',
              background: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              <strong>Selected Location:</strong> {parseFloat(formData.location_lat).toFixed(6)}, {parseFloat(formData.location_lng).toFixed(6)} (Zoom: {formData.zoom_level})
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Festival'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FestivalList;
